import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * `<Pill>` — the unified status / label component proposed in the
 * pcnshark-review design handoff (`pcnshark-review/mockups.html`, §03).
 *
 * Replaces three earlier patterns that all expressed the same idea in
 * different vocabularies:
 *
 *   - `uploads-list.tsx`'s 9-case `statusBadge()` switch (shadcn
 *     destructive / secondary / outline + bespoke emerald / amber / sky
 *     Tailwind classes).
 *   - `app/enterprise/page.tsx`'s `RoadmapStatusPill` (dot + text using
 *     the `--shark-*` tokens).
 *   - `app/security/page.tsx`'s local `StatusPill` (same idea, slightly
 *     different shape).
 *
 * Five tones, deliberately. Each tone maps to a semantic category — not
 * to a specific copy string — so the same component can express
 * "Critical PCN severity" and "Extraction failed" and "Internal email
 * failed" with the same visual treatment because all three are calls to
 * action of equal weight. See review.md §03 for the full mapping.
 *
 * Implementation notes:
 *   - Renders as `<span>` so it nests inline inside cells and headings.
 *   - Dot is a 6px circle in the tone's accent color; text is mono caps
 *     in the tone's text color. No background — the marketing design
 *     system prefers wordmarks over filled chips for status, since
 *     filled red shadcn pills on a list of high-severity rows reads as
 *     "your UI is broken" rather than "these are critical."
 *   - `running` adds a pulse to the dot for in-flight states
 *     (extraction running, syncing, etc.). The mockup uses a custom
 *     1.4s pulse but Tailwind's default `animate-pulse` is close enough
 *     and doesn't require new keyframes.
 *   - Colors are pulled from CSS variables so the token bridge in
 *     `globals.css` controls the actual hue. Switching the brand
 *     palette later is a one-token change, not a sweep through this
 *     file.
 */
export type PillTone = "alert" | "info" | "good" | "neutral" | "muted";

const TONE_STYLES: Record<PillTone, { text: string; dot: string }> = {
  // Action required. Severity Critical/High; extraction Failed.
  alert: {
    text: "text-[color:var(--shark-alert)]",
    dot: "bg-[color:var(--shark-alert)]",
  },
  // In flight, no user action needed. Use with `running` for pulsing
  // dot. Extraction running/pending, "syncing", etc.
  info: {
    text: "text-[color:var(--shark-accent-ink)]",
    dot: "bg-[color:var(--shark-accent)]",
  },
  // Successful terminal state. Extraction completed, "In production"
  // on /enterprise, signup confirmed.
  good: {
    text: "text-[color:var(--shark-good)]",
    dot: "bg-[color:var(--shark-good)]",
  },
  // Acknowledged, no action expected. Severity Medium, "Planned" /
  // "On request" on roadmap, cancelled jobs.
  neutral: {
    text: "text-[color:var(--ink-3)]",
    dot: "bg-[color:var(--ink-3)]",
  },
  // Informational, deprioritised. Email duplicate, no PDFs to extract,
  // Severity Low. Dot uses the lighter --rule-2 to fade further than
  // neutral.
  muted: {
    text: "text-[color:var(--ink-3)]",
    dot: "bg-[color:var(--rule-2)]",
  },
};

export interface PillProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
  tone?: PillTone;
  /** Pulses the dot for in-flight states. Default false. */
  running?: boolean;
  children: React.ReactNode;
}

export function Pill({
  tone = "neutral",
  running = false,
  className,
  children,
  ...props
}: PillProps) {
  const t = TONE_STYLES[tone];
  return (
    <span
      className={cn(
        // Layout: inline-flex so the dot + text sit on the baseline,
        // gap matches the mockup's 6px (gap-1.5 = 6px in Tailwind 3).
        "inline-flex items-center gap-1.5",
        // Type: mono caps with the same tracking the marketing eyebrows
        // use elsewhere. 10px keeps it readable while staying smaller
        // than table-cell body copy (~13–14px).
        "font-mono text-[10px] uppercase tracking-[0.14em] leading-none",
        // Stop status text from wrapping mid-pill in cramped table cells.
        "whitespace-nowrap",
        t.text,
        className,
      )}
      {...props}
    >
      <span
        className={cn(
          "inline-block w-1.5 h-1.5 rounded-full shrink-0",
          t.dot,
          running && "animate-pulse",
        )}
        aria-hidden
      />
      {children}
    </span>
  );
}
